import type { SortField, SortOrder } from '../types';

interface TableHeaderProps {
  sortColumn: SortField | null;
  sortDirection: SortOrder;
  onSort: (column: SortField) => void;
}

interface ColumnConfig {
  key: SortField | string;
  label: string;
  sortable: boolean;
  width: string;
}

const columns: ColumnConfig[] = [
  { key: 'vendor_name', label: 'Third-Party', sortable: true, width: 'w-[240px]' },
  { key: 'risk_level', label: 'Risk', sortable: true, width: 'w-[160px]' },
  { key: 'business_criticality', label: 'Business Criticality', sortable: false, width: 'w-[180px]' },
  { key: 'status' as SortField, label: 'Status', sortable: true, width: 'w-[160px]' },
  { key: 'last_updated_at', label: 'Last Assessed', sortable: true, width: 'w-[160px]' },
];

function SortIcon({ isActive, direction }: { isActive: boolean; direction: SortOrder }) {
  const upColor = isActive && direction === 'asc' ? '#52525B' : '#CAD5E2';
  const downColor = isActive && direction === 'desc' ? '#52525B' : '#CAD5E2';

  return (
    <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 0.5L7.5 4.5H0.5L4 0.5Z" fill={upColor} />
      <path d="M4 11.5L0.5 7.5H7.5L4 11.5Z" fill={downColor} />
    </svg>
  );
}

export function TableHeader({ sortColumn, sortDirection, onSort }: TableHeaderProps) {
  return (
    <div
      role="row"
      className="flex flex-row items-center px-3 pr-4 py-2 gap-3 h-9 bg-[#F8FAFC] border-b border-[#E2E8F0]"
    >
      {/* Chevron spacer - matches the expand chevron in VendorRow */}
      <div className="w-5 h-5 flex-shrink-0" />

      {/* Columns container */}
      <div className="flex flex-row items-center justify-between flex-1 gap-4">
        {columns.map((column) => (
          <div
            key={column.label}
            role="columnheader"
            className={`flex items-center gap-2 ${column.width} ${
              column.sortable ? 'cursor-pointer select-none' : ''
            }`}
            onClick={column.sortable && column.key ? () => onSort(column.key as SortField) : undefined}
          >
            <span className="font-['Inter'] font-semibold text-xs leading-5 text-[#62748E]">
              {column.label}
            </span>
            {column.sortable && (
              <SortIcon
                isActive={column.key === sortColumn}
                direction={sortDirection}
              />
            )}
          </div>
        ))}

        {/* Actions column spacer */}
        <div className="w-5 h-5 flex-shrink-0" />
      </div>
    </div>
  );
}
